import { Fragment, useMemo, useState } from 'react';
import { Carte, BoutonSecondaire, MessageErreur, Pastille } from '../ui/Primitives';
import { Icone } from '../Icone';
import { ErreurApi } from '../../lib/api';
import {
  useApercuBpf,
  urlFichierBpf,
  useReferentiels,
  type ValeurBpf,
} from '../../lib/useDocuments';

/**
 * Exercices proposés : l'année en cours et les quatre précédentes.
 *
 * Le BPF se dépose avant le 31 mai sur l'exercice **clos** ; l'exercice par
 * défaut est donc le précédent. L'année en cours reste proposée — voir où en
 * est le bilan avant sa clôture est utile — mais dite « en cours », pour que
 * personne ne dépose un exercice incomplet en croyant l'avoir arrêté.
 */
function exercicesProposes(aujourdhui: Date): { annee: number; libelle: string }[] {
  const courante = aujourdhui.getFullYear();
  return [0, 1, 2, 3, 4].map((recul) => ({
    annee: courante - recul,
    libelle: recul === 0 ? `${courante} · en cours` : String(courante - recul),
  }));
}

/** Regroupe les valeurs par cadre, en conservant l'ordre du formulaire. */
function parCadre(valeurs: ValeurBpf[]): [string, ValeurBpf[]][] {
  const groupes = new Map<string, ValeurBpf[]>();
  for (const valeur of valeurs) {
    const groupe = groupes.get(valeur.cadre);
    if (groupe) groupe.push(valeur);
    else groupes.set(valeur.cadre, [valeur]);
  }
  return [...groupes];
}

/**
 * Bilan pédagogique et financier (CDC §4.4).
 *
 * Le BPF porte sur l'organisme et se **saisit** au téléservice de la DGEFP :
 * l'écran est donc un plan de saisie, pas un simple bouton de téléchargement.
 * Ce qui compte est autant ce que la plateforme calcule que ce qu'elle ne sait
 * pas remplir — d'où les avertissements en tête et la mention de la source sur
 * chaque rubrique.
 */
export function PanneauBpf({ site }: { site: string }): JSX.Element {
  const [exercice, setExercice] = useState(() => new Date().getFullYear() - 1);
  const [detaille, setDetaille] = useState(false);

  const apercu = useApercuBpf(exercice, site);
  const referentiels = useReferentiels();
  const exercices = useMemo(() => exercicesProposes(new Date()), []);

  const cadres = referentiels.data?.bpf.cadres ?? {};
  const valeurs = apercu.data?.valeurs ?? [];
  const renseignees = valeurs.filter((valeur) => valeur.valeur !== '');

  function chiffre(cle: string): string {
    return valeurs.find((valeur) => valeur.cle === cle)?.valeur ?? '—';
  }

  const formateurs =
    Number(chiffre('e_internes_personnes') || 0) + Number(chiffre('e_externes_personnes') || 0);

  return (
    <Carte
      titre="Récapitulatif de l’exercice"
      actions={
        <label className="flex items-center gap-2 text-xs text-codialis-gray">
          <span className="hidden sm:inline">Exercice</span>
          <select
            className="min-h-[2.25rem] [@media(pointer:coarse)]:min-h-[40px] rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-semibold text-codialis-navy"
            value={exercice}
            onChange={(evenement) => setExercice(Number(evenement.target.value))}
          >
            {exercices.map((propose) => (
              <option key={propose.annee} value={propose.annee}>
                {propose.libelle}
              </option>
            ))}
          </select>
        </label>
      }
      sansBordure
    >
      <div className="space-y-4 p-4 sm:p-6">
        <p className="text-xs text-codialis-gray">
          Bilan annuel déposé auprès de la DGEFP, rubrique par rubrique. La plateforme
          calcule les cadres pédagogiques (E et F) depuis les dossiers et les émargements
          de l’exercice ; les cadres financiers (C et D) viennent de la comptabilité et
          sortent vides.
        </p>

        {apercu.error instanceof ErreurApi && <MessageErreur message={apercu.error.message} />}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Tuile libelle="Stagiaires" valeur={chiffre('f1_total_stagiaires')} />
          <Tuile libelle="Heures-stagiaires" valeur={chiffre('f1_total_heures')} />
          <Tuile
            libelle="Formateurs"
            valeur={apercu.data ? String(formateurs) : '—'}
          />
        </div>

        {(apercu.data?.avertissements ?? []).length > 0 && (
          <ul className="space-y-2 rounded-xl bg-codialis-sand p-3">
            {apercu.data?.avertissements.map((avertissement) => (
              <li key={avertissement} className="flex gap-2 text-[11px] text-codialis-navy">
                <Icone nom="triangleAlerte" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{avertissement}</span>
              </li>
            ))}
          </ul>
        )}

        {detaille && (
          <div
            className="relative max-h-96 overflow-auto rounded-xl border border-gray-100"
            role="region"
            aria-label={`Rubriques du récapitulatif ${exercice}`}
            tabIndex={0}
          >
            <table className="w-full min-w-[34rem] border-collapse text-left text-[11px]">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-gray-100 text-codialis-gray">
                  <th className="px-3 py-2 font-semibold">Rubrique</th>
                  <th className="px-3 py-2 text-right font-semibold">Valeur</th>
                  <th className="px-3 py-2 font-semibold">Source</th>
                </tr>
              </thead>
              <tbody>
                {parCadre(valeurs).map(([cadre, lignes]) => (
                  <FragmentCadre
                    key={cadre}
                    intitule={cadres[cadre] ?? cadre}
                    lignes={lignes}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <BoutonSecondaire
            onClick={() => {
              window.location.href = urlFichierBpf(exercice, site);
            }}
          >
            Télécharger le récapitulatif {exercice}
          </BoutonSecondaire>
          <BoutonSecondaire onClick={() => setDetaille((ouvert) => !ouvert)}>
            {detaille ? 'Masquer le détail' : 'Voir les rubriques'}
          </BoutonSecondaire>
          {apercu.data && (
            <span className="text-[11px] text-codialis-gray">
              {renseignees.length} rubrique(s) calculée(s),{' '}
              {apercu.data.rubriquesVides.length} à compléter à la saisie.
            </span>
          )}
        </div>
      </div>
    </Carte>
  );
}

function Tuile({ libelle, valeur }: { libelle: string; valeur: string }): JSX.Element {
  return (
    <div className="rounded-xl bg-codialis-sand p-3">
      <div className="text-[10px] uppercase tracking-wide text-codialis-gray">{libelle}</div>
      <div className="font-display text-xl font-bold text-codialis-navy">{valeur}</div>
    </div>
  );
}

function FragmentCadre({
  intitule,
  lignes,
}: {
  intitule: string;
  lignes: ValeurBpf[];
}): JSX.Element {
  return (
    <>
      <tr className="bg-gray-50">
        <td colSpan={3} className="px-3 py-1.5 font-semibold text-codialis-navy">
          {intitule}
        </td>
      </tr>
      {lignes.map((ligne, rang) => (
        <Fragment key={ligne.cle}>
          {ligne.groupe && ligne.groupe !== lignes[rang - 1]?.groupe && (
            <tr>
              <td colSpan={3} className="px-3 pt-2 pb-0.5 italic text-codialis-gray">
                {ligne.groupe}
              </td>
            </tr>
          )}
          <LigneRubrique ligne={ligne} />
        </Fragment>
      ))}
    </>
  );
}

function LigneRubrique({ ligne }: { ligne: ValeurBpf }): JSX.Element {
  return (
    <tr className="border-b border-gray-50 last:border-0">
      <td className={`px-3 py-1.5 text-codialis-navy ${ligne.total ? 'font-semibold' : ''}`}>
        {ligne.repere && <span className="mr-1.5 font-semibold">{ligne.repere}</span>}
        {ligne.libelle}
        {ligne.unite && (
          <span className="ml-1 font-normal text-codialis-gray">({ligne.unite})</span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-right font-semibold text-codialis-navy">
        {ligne.valeur === '' ? (
          <Pastille texte="à saisir" ton="warning" />
        ) : (
          ligne.valeur
        )}
      </td>
      <td className="px-3 py-1.5 text-codialis-gray">{ligne.origine}</td>
    </tr>
  );
}
